var Model = require("../types/Model.js");
var extender = Model.makeExtender;
var xtend = require("xtend");
// eslint-disable-next-line node/no-missing-require
const AsyncStorage = require("@react-native-async-storage/async-storage").default;

/**
 * The model used for handling logins with an email/password
 * @type {Model}
 */
module.exports = Model({
	name: "login",
	init: init,
	listeners: listeners,
	triggers: triggers,

	load_token: load_token,
});

function init() {

	this.push({
		authenticating: false,
		message: "",
		token: "",
		language: "en",
		localization: {}, // The current localization file
		asked: false,
		save: false,
		has_handled: false,
	});

	this.load_token();
}

function triggers() {
	return {
		"login": handle_trigger_login,
		"show": handle_trigger_show,
		"asked": handle_trigger_ask,
		"twofactorauth": handle_two_factor_auth,
		"twofactorauth:cancel": handle_two_factor_auth_cancel,
		"twofactorauth:request": handle_two_factor_auth_request,
	};
}

function listeners() {
	return {
		"localization:updated": handle_localization_update,
		"toast:updated": extender("toast"),
		"save:updated": extender("save"),
		"twofactorauth:updated": extender("twofactorauth"),
		"machinetracker:updated": handle_machine_tracker_update
	};
}

/**
 * This trigger should be triggered by the UI to attempt a login
 * @param  {Object} data Should contain the `email` and `password` for logging in
 */
function handle_trigger_login(data) {
	var email = data.email;
	var password = data.password;

	this.emit("save:trigger:start");

	this.api.auth.login(email, password)
		.then(handle_login_success.bind(this))
		.catch(handle_login_fail.bind(this));

	this.emit("usermetrics:trigger:track", "login");
}

function handle_trigger_show() {
	this.extend({
		asked: true
	});
	this.emit("usermetrics:trigger:track", "navigate to password login screen");
}

function handle_two_factor_auth(data) {
	var code = data.authcode;
	var id = data.id;
	this.emit("save:trigger:start");

	this.api.auth.twoFactorAuth(id, code)
		.then(handle_two_factor_auth_success.bind(this))
		.catch(handle_two_factor_auth_fail.bind(this));

	this.emit("usermetrics:trigger:track", "begin two factor login");
}

/**
 * 	Sets 'asked' to 'false'.  Used in desktop login page.
 */
function handle_trigger_ask() {
	this.emit("usermetrics:trigger:track", "navigate to welcome page");
	this.extend({
		asked: false
	});
}

/**
 * Whenever the machinetracker updates, fetch the token and display it
 * @param  {Object} data The state of MachineTracker. Contains `serial`, `apk`, and sometimes `token`
 */
function handle_machine_tracker_update(data) {
	if (this.data.has_handled) return;
	var token = data.token;
	if (token)
		AsyncStorage.setItem("token", token).then(function(){
			this.load_token();
		}.bind(this));
	else this.emit("machinetracker:trigger:recover");
}

/**
 * Called when logging in succeeds
 * @param  {Object} data The result of the login call. Contains `token`
 */
function handle_login_success(data) {
	this.emit("save:trigger:finish");
	if(data.multifactor) {
		this.extend({
			id: data.id,
			email: data.email
		});
	} else {
		AsyncStorage.setItem("passwordLogin", "true");
		AsyncStorage.setItem("token", data.token).then(function () {
			this.load_token();
		}.bind(this));
	}

}

function handle_two_factor_auth_success(data) {
	this.emit("save:trigger:finish");
	this.extend(data);
	AsyncStorage.setItem("passwordLogin", "true");
	AsyncStorage.setItem("token", data.token).then(function () {
		this.load_token();
	}.bind(this));
}

function handle_two_factor_auth_fail(){
	this.emit("save:trigger:finish");
	const message = "login.authcodeerror";
	this.extend({ message });
	this.emit("toast:trigger:show", {
		content: message,
		style: "error"
	});
}

/**
 * Called when logging in fails
 * @param  {Error} error The error that happened. S
 */
function handle_login_fail(error) {
	this.emit("save:trigger:finish");

	var status = error.status;
	var status_map = {
		0: "login.loginerror1",
		400: "login.loginerror0",
		401: "login.loginerror5",
		404: "login.loginerror3"
	};

	var message = status_map[status] || "login.loginerror1";
	if (message) this.extend({
		message: message
	});
	this.emit("toast:trigger:show", {
		content: message,
		style: "error"
	});
}

/**
 * Called when a successful token has been processed.
 * Updates the state with the new token
 * @param  {String} token The token
 */
function handle_token(token, data) {
	AsyncStorage.setItem("token", token).then(function () {
		this.extend(xtend(data, {
			token: token,
			has_handled: true
		}));
	}.bind(this));
}

/**
 * Attempts to load the token in AsyncStorage.
 * If the token authenticates successfully, it is used in handle_token
 * Sets `authenticating` to true while the HTTP request is in progress
 */
function load_token() {
	return AsyncStorage.getItem("token").then(function (saved_token) {
		if (!saved_token) return;
		this.extend({
			authenticating: true,
		});
		this.api.auth
			.auth(saved_token)
			.then(handle_token.bind(this, saved_token))
			.catch(handle_load_token_fail.bind(this));
	}.bind(this));
}

/**
 * Handles the situation where an invalid auth token was saved in AsyncStorage.
 * Displays an error to the user, hides loading spinner
 * @param  {Error} error The Error that occured
 */
function handle_load_token_fail(error) {
	// Try again in 30 seconds
	setTimeout(this.load_token.bind(this), 1000 * 30);

	var status = error.status;
	var status_map = {
		0: "login.loginerror1",
		401: "login.loginerror5"
	};

	var message = status_map[status];

	this.extend({
		authenticating: false,
		message: message
	});
	this.emit("toast:trigger:show", {
		content: message,
		style: "error"
	});
}

/**
 * Handles changes in localization. Updates `localization` state.
 * @param  {Object} data The state of the Localization model. Contains `localization` map.
 */
function handle_localization_update(data) {
	this.extend({
		localization: data.localization,
		language: data.language,
	});
}


function handle_two_factor_auth_cancel() {
	this.extend({
		id: "",
		email: ""
	});
	this.emit("usermetrics:trigger:track", "cancel two factor login");
}


function handle_two_factor_auth_request(data) {
	var email = data.email;
	var id = data.id;
	this.extend({
		sending: true
	});

	this.api.auth
		.twoFactorAuthRequest(id, email)
		.then(handle_two_factor_auth_request_success.bind(this))
		.catch(handle_two_factor_auth_request_error.bind(this));

	this.emit("usermetrics:trigger:track", "two factor login");
}

/**
 * Marks auto code request sending as successful
 */
function handle_two_factor_auth_request_success() {
	this.extend({
		sending: false,
		sent: true
	});
}

/**
 * Handles errors during sending a auto code
 */
function handle_two_factor_auth_request_error(err) {
	var code = err.status;
	var message = "multifactor.errorunknown";
	var error_map = {
		400: "multifactor.error400",
		404: "multifactor.error404"
	};

	if (code in error_map)
		message = error_map[code];

	this.extend({
		sending: false,
		message: message
	});
	this.emit("toast:trigger:show", {
		content: message,
		style: "error"
	});
}
